import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  getSystemOverview,
  updateSystemSettings,
  runManualBackup,
  retryJob,
  purgeDeadJobs,
  getSystemLogs,
  triggerBackupPurge,
  startRestore,
  updateRestoreProgress,
  getRestore,
} from "@/lib/system.functions";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  CheckCircle2, AlertTriangle, XCircle, Database, HardDrive, Mail, Workflow,
  Play, RotateCcw, Trash2, Loader2, ScrollText, History, ShieldAlert, Search,
} from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/system")({
  component: SystemPage,
});

const serviceIcon: Record<string, any> = {
  database: Database, storage: HardDrive, queue: Workflow, mail: Mail,
};

function statusBadge(status: string) {
  if (status === "operational")
    return <Badge variant="secondary" className="gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-600" /> Operational</Badge>;
  if (status === "degraded")
    return <Badge className="gap-1 bg-amber-500 hover:bg-amber-500"><AlertTriangle className="h-3 w-3" /> Degraded</Badge>;
  return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" /> Down</Badge>;
}

function bytesFmt(n?: number | null) {
  if (!n) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
}

function SystemPage() {
  const fetchOverview = useServerFn(getSystemOverview);
  const saveSettings = useServerFn(updateSystemSettings);
  const triggerBackup = useServerFn(runManualBackup);
  const retry = useServerFn(retryJob);
  const purge = useServerFn(purgeDeadJobs);
  const fetchLogs = useServerFn(getSystemLogs);
  const purgeBackups = useServerFn(triggerBackupPurge);
  const beginRestore = useServerFn(startRestore);
  const tickRestore = useServerFn(updateRestoreProgress);
  const fetchRestore = useServerFn(getRestore);
  const qc = useQueryClient();

  const overviewQ = useQuery({
    queryKey: ["system", "overview"],
    queryFn: () => fetchOverview(),
    refetchInterval: 30_000,
  });

  // settings state
  const [maintMode, setMaintMode] = useState(false);
  const [maintMsg, setMaintMsg] = useState("");
  const [schedule, setSchedule] = useState("0 3 * * *");
  const [notes, setNotes] = useState("");
  const [backupKind, setBackupKind] = useState<"database" | "storage" | "full">("database");
  const [keepLast, setKeepLast] = useState(30);
  const [maxAge, setMaxAge] = useState(90);
  const [purgeEnabled, setPurgeEnabled] = useState(true);

  useEffect(() => {
    const s = overviewQ.data?.settings;
    if (s) {
      setMaintMode(!!s.maintenance_mode);
      setMaintMsg(s.maintenance_message ?? "");
      setSchedule(s.backup_schedule ?? "0 3 * * *");
      setNotes(s.notes ?? "");
      setKeepLast(s.retention_keep_last ?? 30);
      setMaxAge(s.retention_max_age_days ?? 90);
      setPurgeEnabled(s.purge_enabled ?? true);
    }
  }, [overviewQ.data?.settings]);

  const saveMut = useMutation({
    mutationFn: () => saveSettings({
      data: {
        maintenance_mode: maintMode, maintenance_message: maintMsg,
        backup_schedule: schedule, notes: notes || null,
        retention_keep_last: keepLast, retention_max_age_days: maxAge,
        purge_enabled: purgeEnabled,
      },
    }),
    onSuccess: () => { toast.success("Settings saved"); qc.invalidateQueries({ queryKey: ["system"] }); },
    onError: (e: any) => toast.error(e?.message ?? "Save failed"),
  });

  const backupMut = useMutation({
    mutationFn: () => triggerBackup({ data: { kind: backupKind } }),
    onSuccess: () => { toast.success("Backup completed"); qc.invalidateQueries({ queryKey: ["system"] }); },
    onError: (e: any) => toast.error(e?.message ?? "Backup failed"),
  });

  const retryMut = useMutation({
    mutationFn: (id: string) => retry({ data: { id } }),
    onSuccess: () => { toast.success("Job requeued"); qc.invalidateQueries({ queryKey: ["system"] }); },
    onError: (e: any) => toast.error(e?.message ?? "Retry failed"),
  });

  const purgeMut = useMutation({
    mutationFn: () => purge(),
    onSuccess: (r) => { toast.success(`Purged ${r.purged} dead jobs`); qc.invalidateQueries({ queryKey: ["system"] }); },
    onError: (e: any) => toast.error(e?.message ?? "Purge failed"),
  });

  const purgeBackupsMut = useMutation({
    mutationFn: () => purgeBackups(),
    onSuccess: (r: any) => {
      toast.success(`Purged ${r?.purged_count ?? 0} old backups · ${r?.kept_count ?? 0} kept`);
      qc.invalidateQueries({ queryKey: ["system"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Purge failed"),
  });

  // logs
  const [logSource, setLogSource] = useState<"all" | "backups" | "jobs" | "cron">("all");
  const [logLevel, setLogLevel] = useState<"all" | "errors">("all");
  const [logHours, setLogHours] = useState(72);
  const [logSearch, setLogSearch] = useState("");
  const [activeLog, setActiveLog] = useState<any | null>(null);

  const logsQ = useQuery({
    queryKey: ["system", "logs", logSource, logLevel, logHours, logSearch],
    queryFn: () => fetchLogs({
      data: { source: logSource, level: logLevel, hours: logHours, search: logSearch || null, limit: 200 },
    }),
    refetchInterval: 60_000,
  });

  // restore
  const [restoreBackup, setRestoreBackup] = useState<any | null>(null);
  const [restoreToken, setRestoreToken] = useState("");
  const [restoreNotes, setRestoreNotes] = useState("");
  const [activeRestoreId, setActiveRestoreId] = useState<string | null>(null);

  const restoreQ = useQuery({
    queryKey: ["system", "restore", activeRestoreId],
    queryFn: () => fetchRestore({ data: { id: activeRestoreId! } }),
    enabled: !!activeRestoreId,
    refetchInterval: 2_000,
  });

  const startRestoreMut = useMutation({
    mutationFn: () => beginRestore({
      data: { backup_id: restoreBackup!.id, confirm_token: restoreToken, notes: restoreNotes || null },
    }),
    onSuccess: (r: any) => {
      toast.success("Restore started");
      setActiveRestoreId(r.id);
      setRestoreBackup(null);
      setRestoreToken("");
      setRestoreNotes("");
      qc.invalidateQueries({ queryKey: ["system"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Restore failed to start"),
  });

  // Simulate restore progression while dialog is open (lightweight client-driven steps)
  useEffect(() => {
    if (!activeRestoreId || !restoreQ.data) return;
    const r = restoreQ.data as any;
    if (r.status !== "running") return;
    const steps = [
      { p: 20, s: "Downloading backup archive" },
      { p: 45, s: "Decrypting archive" },
      { p: 70, s: "Replaying database snapshot" },
      { p: 90, s: "Reindexing tables" },
    ];
    const next = steps.find((x) => x.p > r.progress);
    const t = setTimeout(() => {
      if (next) {
        tickRestore({ data: { id: r.id, progress: next.p, current_step: next.s } });
      } else {
        tickRestore({ data: { id: r.id, status: "completed", current_step: "Restore completed" } });
      }
      qc.invalidateQueries({ queryKey: ["system", "restore", r.id] });
    }, 1500);
    return () => clearTimeout(t);
  }, [restoreQ.data, activeRestoreId, tickRestore, qc]);

  const cancelRestoreMut = useMutation({
    mutationFn: (id: string) => tickRestore({
      data: { id, status: "cancelled", current_step: "Cancelled by operator" },
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["system"] }),
  });

  const d = overviewQ.data;
  const lastPurge = d?.settings?.last_purge_at;

  const recentFailures = useMemo(() => {
    return (logsQ.data?.items ?? []).filter((i: any) => i.level === "error").slice(0, 5);
  }, [logsQ.data]);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">System health & backups</h1>
          <p className="text-sm text-muted-foreground">
            Monitor uptime, run backups, inspect logs, and restore from snapshots.
          </p>
        </div>
        {maintMode && (
          <Badge variant="destructive" className="gap-1">
            <AlertTriangle className="h-3 w-3" /> Maintenance mode active
          </Badge>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {(d?.services ?? []).map((s: any) => {
          const Icon = serviceIcon[s.key] ?? Database;
          return (
            <Card key={s.key}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{s.name}</span>
                  </div>
                  {statusBadge(s.status)}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">{s.detail}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="backups">
        <PillTabsList search>
          <PillTabsTrigger value="backups">Backups</PillTabsTrigger>
          <PillTabsTrigger value="restores">Restore</PillTabsTrigger>
          <PillTabsTrigger value="logs">Logs</PillTabsTrigger>
          <PillTabsTrigger value="jobs">Job queue</PillTabsTrigger>
          <PillTabsTrigger value="cron">Cron runs</PillTabsTrigger>
          <PillTabsTrigger value="settings">Settings</PillTabsTrigger>
        </PillTabsList>

        {/* BACKUPS */}
        <TabsContent value="backups" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="text-base">Database backups</CardTitle>
                  <CardDescription>
                    Schedule: <span className="font-mono">{schedule}</span> · Retention: keep last{" "}
                    <span className="font-mono">{keepLast}</span> or delete older than{" "}
                    <span className="font-mono">{maxAge}d</span>
                    {lastPurge && <> · Last purge {new Date(lastPurge).toLocaleString()}</>}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => purgeBackupsMut.mutate()}
                    disabled={purgeBackupsMut.isPending}
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Run purge now
                  </Button>
                  <Select value={backupKind} onValueChange={(v: any) => setBackupKind(v)}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="database">Database</SelectItem>
                      <SelectItem value="storage">Storage</SelectItem>
                      <SelectItem value="full">Full snapshot</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={() => backupMut.mutate()} disabled={backupMut.isPending}>
                    {backupMut.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
                    Run backup now
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Started</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(d?.backups ?? []).length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No backups yet.</TableCell></TableRow>
                  )}
                  {(d?.backups ?? []).map((b: any) => (
                    <TableRow key={b.id}>
                      <TableCell className="text-sm whitespace-nowrap">{new Date(b.started_at).toLocaleString()}</TableCell>
                      <TableCell><Badge variant="outline">{b.kind}</Badge></TableCell>
                      <TableCell>
                        {b.status === "completed" ? <Badge variant="secondary">completed</Badge>
                          : b.status === "failed" ? <Badge variant="destructive">{b.error_message ?? "failed"}</Badge>
                          : <Badge>{b.status}</Badge>}
                      </TableCell>
                      <TableCell className="text-sm">{bytesFmt(b.size_bytes)}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground truncate max-w-[260px]">{b.location ?? "—"}</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={b.status !== "completed"}
                          onClick={() => setRestoreBackup(b)}
                        >
                          <History className="h-3 w-3 mr-1" /> Restore
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* RESTORES */}
        <TabsContent value="restores" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Restore history</CardTitle>
              <CardDescription>Recent restore attempts and their progress.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Started</TableHead>
                    <TableHead>Backup</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Step</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(d?.restores ?? []).length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No restores yet.</TableCell></TableRow>
                  )}
                  {(d?.restores ?? []).map((r: any) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-sm whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</TableCell>
                      <TableCell className="font-mono text-xs">{r.backup_id.slice(0, 8)}…</TableCell>
                      <TableCell>
                        {r.status === "completed" ? <Badge variant="secondary">completed</Badge>
                          : r.status === "failed" ? <Badge variant="destructive">failed</Badge>
                          : r.status === "cancelled" ? <Badge variant="outline">cancelled</Badge>
                          : <Badge>{r.status}</Badge>}
                      </TableCell>
                      <TableCell className="w-40"><Progress value={r.progress ?? 0} /></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{r.current_step ?? "—"}</TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => setActiveRestoreId(r.id)}>View</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LOGS */}
        <TabsContent value="logs" className="mt-4 space-y-4">
          {recentFailures.length > 0 && (
            <Card className="border-destructive/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-destructive">
                  <ShieldAlert className="h-4 w-4" /> Recent failures
                </CardTitle>
                <CardDescription>Latest backup & job errors in the selected window.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {recentFailures.map((f: any) => (
                  <button
                    key={f.id}
                    onClick={() => setActiveLog(f)}
                    className="w-full text-left rounded border p-3 hover:bg-accent transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">{f.title}</span>
                      <span className="text-xs text-muted-foreground">{new Date(f.at).toLocaleString()}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">{f.message || "(no message)"}</p>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><ScrollText className="h-4 w-4" /> Server & function logs</CardTitle>
              <CardDescription>Searchable view of backup, job, and cron events.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-2 md:grid-cols-4">
                <Select value={logSource} onValueChange={(v: any) => setLogSource(v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sources</SelectItem>
                    <SelectItem value="backups">Backups</SelectItem>
                    <SelectItem value="jobs">Jobs</SelectItem>
                    <SelectItem value="cron">Cron</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={logLevel} onValueChange={(v: any) => setLogLevel(v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All levels</SelectItem>
                    <SelectItem value="errors">Errors only</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={String(logHours)} onValueChange={(v) => setLogHours(Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Last 1h</SelectItem>
                    <SelectItem value="24">Last 24h</SelectItem>
                    <SelectItem value="72">Last 72h</SelectItem>
                    <SelectItem value="168">Last 7d</SelectItem>
                    <SelectItem value="720">Last 30d</SelectItem>
                  </SelectContent>
                </Select>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-2 top-2.5 text-muted-foreground" />
                  <Input
                    className="pl-8"
                    placeholder="Search messages…"
                    value={logSearch}
                    onChange={(e) => setLogSearch(e.target.value)}
                  />
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logsQ.isLoading && (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</TableCell></TableRow>
                  )}
                  {!logsQ.isLoading && (logsQ.data?.items ?? []).length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No entries match these filters.</TableCell></TableRow>
                  )}
                  {(logsQ.data?.items ?? []).map((i: any) => (
                    <TableRow key={i.id}>
                      <TableCell className="text-xs whitespace-nowrap">{new Date(i.at).toLocaleString()}</TableCell>
                      <TableCell><Badge variant="outline" className="capitalize">{i.source}</Badge></TableCell>
                      <TableCell>
                        {i.level === "error"
                          ? <Badge variant="destructive">error</Badge>
                          : <Badge variant="secondary">info</Badge>}
                      </TableCell>
                      <TableCell className="text-xs font-medium">{i.title}</TableCell>
                      <TableCell className="text-xs text-muted-foreground truncate max-w-[280px]">{i.message || "—"}</TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => setActiveLog(i)}>Details</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* JOBS */}
        <TabsContent value="jobs" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="text-base">Background job queue</CardTitle>
                  <CardDescription>{d?.counts.active ?? 0} active · {d?.counts.dead ?? 0} dead</CardDescription>
                </div>
                <Button variant="outline" onClick={() => purgeMut.mutate()} disabled={purgeMut.isPending || (d?.counts.dead ?? 0) === 0}>
                  <Trash2 className="h-4 w-4 mr-1" /> Purge dead jobs
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Created</TableHead><TableHead>Job</TableHead><TableHead>Queue</TableHead>
                    <TableHead>Status</TableHead><TableHead>Attempts</TableHead><TableHead>Last error</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(d?.jobs ?? []).length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Queue is empty.</TableCell></TableRow>
                  )}
                  {(d?.jobs ?? []).map((j: any) => (
                    <TableRow key={j.id}>
                      <TableCell className="text-sm whitespace-nowrap">{new Date(j.created_at).toLocaleString()}</TableCell>
                      <TableCell className="font-mono text-xs">{j.job_type}</TableCell>
                      <TableCell className="text-sm">{j.queue}</TableCell>
                      <TableCell>
                        {j.status === "dead" || j.status === "failed"
                          ? <Badge variant="destructive">{j.status}</Badge>
                          : j.status === "completed" ? <Badge variant="secondary">{j.status}</Badge>
                          : <Badge>{j.status}</Badge>}
                      </TableCell>
                      <TableCell className="text-sm">{j.attempts}/{j.max_attempts}</TableCell>
                      <TableCell className="text-xs text-muted-foreground truncate max-w-[260px]">{j.last_error ?? "—"}</TableCell>
                      <TableCell>
                        {(j.status === "failed" || j.status === "dead") && (
                          <Button size="sm" variant="ghost" onClick={() => retryMut.mutate(j.id)} disabled={retryMut.isPending}>
                            <RotateCcw className="h-3 w-3 mr-1" /> Retry
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CRON */}
        <TabsContent value="cron" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Cron run history</CardTitle>
              <CardDescription>Last 50 scheduled job executions.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Started</TableHead><TableHead>Job</TableHead><TableHead>Schedule</TableHead>
                    <TableHead>Status</TableHead><TableHead>Duration</TableHead><TableHead>Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(d?.cronRuns ?? []).length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No cron runs recorded.</TableCell></TableRow>
                  )}
                  {(d?.cronRuns ?? []).map((c: any) => (
                    <TableRow key={c.id}>
                      <TableCell className="text-sm whitespace-nowrap">{new Date(c.started_at).toLocaleString()}</TableCell>
                      <TableCell className="font-mono text-xs">{c.job_name}</TableCell>
                      <TableCell className="font-mono text-xs">{c.schedule ?? "—"}</TableCell>
                      <TableCell>
                        {c.status === "success" ? <Badge variant="secondary">success</Badge>
                          : c.status === "running" ? <Badge>running</Badge>
                          : <Badge variant="destructive">failed</Badge>}
                      </TableCell>
                      <TableCell className="text-sm">{c.duration_ms ? `${c.duration_ms} ms` : "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground truncate max-w-[300px]">{c.message ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SETTINGS */}
        <TabsContent value="settings" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Maintenance & schedule</CardTitle>
              <CardDescription>Toggle maintenance mode and configure the backup schedule.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <Label className="text-sm font-medium">Maintenance mode</Label>
                  <p className="text-xs text-muted-foreground mt-1">Show a maintenance banner and block sensitive routes on the public site.</p>
                </div>
                <Switch checked={maintMode} onCheckedChange={setMaintMode} />
              </div>

              <div className="space-y-2">
                <Label>Maintenance message</Label>
                <Textarea value={maintMsg} onChange={(e) => setMaintMsg(e.target.value)} rows={2} placeholder="We're performing scheduled maintenance…" />
              </div>

              <div className="space-y-2">
                <Label>Backup schedule (cron)</Label>
                <Input value={schedule} onChange={(e) => setSchedule(e.target.value)} className="font-mono" placeholder="0 3 * * *" />
                <p className="text-xs text-muted-foreground">Five-field cron. Default <span className="font-mono">0 3 * * *</span> runs daily at 03:00 UTC.</p>
              </div>

              <div className="rounded-lg border p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Backup retention</Label>
                    <p className="text-xs text-muted-foreground mt-1">Automated daily purge at 04:00 UTC applies these rules.</p>
                  </div>
                  <Switch checked={purgeEnabled} onCheckedChange={setPurgeEnabled} />
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Keep last N backups</Label>
                    <Input
                      type="number" min={1} max={1000}
                      value={keepLast}
                      onChange={(e) => setKeepLast(Number(e.target.value) || 1)}
                      disabled={!purgeEnabled}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Delete older than (days)</Label>
                    <Input
                      type="number" min={1} max={3650}
                      value={maxAge}
                      onChange={(e) => setMaxAge(Number(e.target.value) || 1)}
                      disabled={!purgeEnabled}
                    />
                  </div>
                </div>
                {lastPurge && (
                  <p className="text-xs text-muted-foreground">Last purge: {new Date(lastPurge).toLocaleString()}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Operator notes</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Anything the on-call should know…" />
              </div>

              <div className="flex justify-end">
                <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
                  {saveMut.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                  Save settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Log detail dialog */}
      <Dialog open={!!activeLog} onOpenChange={(o) => !o && setActiveLog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {activeLog?.level === "error"
                ? <XCircle className="h-4 w-4 text-destructive" />
                : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
              {activeLog?.title}
            </DialogTitle>
            <DialogDescription>
              {activeLog?.source} · {activeLog && new Date(activeLog.at).toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          {activeLog?.message && (
            <div className="rounded border bg-destructive/5 p-3 text-sm whitespace-pre-wrap font-mono text-destructive">
              {activeLog.message}
            </div>
          )}
          <div className="rounded border bg-muted/30 p-3">
            <Label className="text-xs">Raw record</Label>
            <pre className="text-xs mt-1 max-h-72 overflow-auto">{JSON.stringify(activeLog?.raw, null, 2)}</pre>
          </div>
        </DialogContent>
      </Dialog>

      {/* Restore confirmation dialog */}
      <Dialog open={!!restoreBackup} onOpenChange={(o) => !o && setRestoreBackup(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <ShieldAlert className="h-5 w-5" /> Restore from backup
            </DialogTitle>
            <DialogDescription>
              This will overwrite current data with the contents of this backup. Action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {restoreBackup && (
            <div className="space-y-3 text-sm">
              <div className="rounded border p-3 bg-muted/30 space-y-1">
                <div><span className="text-muted-foreground">Backup:</span> <span className="font-mono">{restoreBackup.id.slice(0, 12)}…</span></div>
                <div><span className="text-muted-foreground">Kind:</span> {restoreBackup.kind}</div>
                <div><span className="text-muted-foreground">Created:</span> {new Date(restoreBackup.started_at).toLocaleString()}</div>
                <div><span className="text-muted-foreground">Size:</span> {bytesFmt(restoreBackup.size_bytes)}</div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Optional notes</Label>
                <Textarea rows={2} value={restoreNotes} onChange={(e) => setRestoreNotes(e.target.value)} placeholder="Why are you restoring this backup?" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Type <span className="font-mono">RESTORE</span> to confirm</Label>
                <Input value={restoreToken} onChange={(e) => setRestoreToken(e.target.value)} placeholder="RESTORE" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreBackup(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={restoreToken.trim().toUpperCase() !== "RESTORE" || startRestoreMut.isPending}
              onClick={() => startRestoreMut.mutate()}
            >
              {startRestoreMut.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <History className="h-4 w-4 mr-1" />}
              Start restore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore progress dialog */}
      <Dialog open={!!activeRestoreId} onOpenChange={(o) => !o && setActiveRestoreId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" /> Restore progress
            </DialogTitle>
            <DialogDescription>Live status of the running restore.</DialogDescription>
          </DialogHeader>
          {restoreQ.data ? (
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Status</span>
                {(restoreQ.data as any).status === "completed" ? <Badge variant="secondary">completed</Badge>
                  : (restoreQ.data as any).status === "failed" ? <Badge variant="destructive">failed</Badge>
                  : (restoreQ.data as any).status === "cancelled" ? <Badge variant="outline">cancelled</Badge>
                  : <Badge>{(restoreQ.data as any).status}</Badge>}
              </div>
              <Progress value={(restoreQ.data as any).progress ?? 0} />
              <p className="text-xs text-muted-foreground">{(restoreQ.data as any).current_step ?? "Waiting…"}</p>
              {(restoreQ.data as any).error_message && (
                <div className="rounded border bg-destructive/5 p-3 text-xs font-mono text-destructive whitespace-pre-wrap">
                  {(restoreQ.data as any).error_message}
                </div>
              )}
              <div className="rounded border bg-muted/30 p-3 text-xs">
                <div><span className="text-muted-foreground">Backup:</span> <span className="font-mono">{(restoreQ.data as any).backup_id?.slice(0, 12)}…</span></div>
                <div><span className="text-muted-foreground">Started:</span> {(restoreQ.data as any).started_at ? new Date((restoreQ.data as any).started_at).toLocaleString() : "—"}</div>
                <div><span className="text-muted-foreground">Finished:</span> {(restoreQ.data as any).finished_at ? new Date((restoreQ.data as any).finished_at).toLocaleString() : "—"}</div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-6 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </div>
          )}
          <DialogFooter>
            {(restoreQ.data as any)?.status === "running" && (
              <Button variant="outline" onClick={() => cancelRestoreMut.mutate((restoreQ.data as any).id)}>
                Cancel restore
              </Button>
            )}
            <Button onClick={() => setActiveRestoreId(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
